import React, { useContext, useMemo } from 'react';

import { FormikProps } from 'formik';

import FormulaTemplateSelector from '../../components/formulas/FormulaTemplateSelector';
import FormulaSettingsSelect from '../../components/formulas/settings/FormulaSettingsSelect';
import { SearchableSelectType } from 'components/inputs/searchableSelect';
// components
import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';

// contexts
import { EditorContext } from '../../context/EditorContext';

import useFormulaUpdateBaseMutation from 'hooks/formulas/useFormulaUpdateBaseMutation';
import useBetaMode from 'hooks/helper/useBetaMode';
import useRewardFunctions from 'hooks/rewardFunctions/useRewardFunctions';

// models
import {
    FormulaProperty,
    FormulaViewSetupFormValue,
} from '../../models/formula.model';

import TopicListDropdown from 'views/monitoring/topicManagement/TopicListDropdown';
import RewardFunctionSelect from 'views/rewardFunctions/RewardFunctionSelect';

import FormulaViewSetupForm from './FormulaViewSetupForm';
import FormulaFromInput from './formulaCreateModal/FormulaFromInput';
import FormulaNameInput from './formulaCreateModal/FormulaNameInput';
import FormulaLabelsSection from './formulaSettings/FormulaLabelsSection';

export interface FormulaSetupProps {
    onTitleChangeMutation?: any;
    formikProps?: FormikProps<FormulaViewSetupFormValue>;
    sampleName?: string;
    onTemplateIdChange?: (templateID: string, isAampeCustom: boolean) => void;
    hasWriteAccess: boolean;
}

const FormulaSetup: React.FC<React.PropsWithChildren<FormulaSetupProps>> = ({
    onTitleChangeMutation,
    formikProps,
    sampleName,
    onTemplateIdChange,
    hasWriteAccess,
}) => {
    const {
        editorDetails: { formula, formulaType },
    } = useContext(EditorContext);
    const { data: rewardFunctions } = useRewardFunctions();

    const isDisabled = useMemo<boolean>(() => {
        return !hasWriteAccess;
    }, [hasWriteAccess]);

    const updateFormulaMutation = useFormulaUpdateBaseMutation(
        formula?.id as number,
    );

    const handleChange = (value: string) => {
        if (formula?.id) {
            updateFormulaMutation.mutate({
                id: formula?.id as number,
                topic_id: value.trim() || null,
            });
        } else {
            onTitleChangeMutation({ topic_id: value.trim() || null });
        }
    };

    const { active: isBetaMode } = useBetaMode();

    return (
        <Container>
            {!formikProps ? (
                <>
                    <TagsTitleContainer>
                        <FormulaNameInput
                            onTitleChangeMutation={onTitleChangeMutation}
                            formulaName={
                                sampleName || (formula?.name as string)
                            }
                            isDisabled={isDisabled}
                        />
                        <FormulaSettingsSelect
                            filterKey={FormulaProperty.Tags}
                            isDisabled={isDisabled}
                        />
                    </TagsTitleContainer>
                    <TagsTitleContainer>
                        <FormulaSettingsSelect
                            filterKey={FormulaProperty.Audience}
                            isDisabled={isDisabled}
                        />

                        <FormulaSettingsSelect
                            filterKey={FormulaProperty.CompoundTrigger}
                            isDisabled={isDisabled}
                        />
                    </TagsTitleContainer>
                    {isBetaMode ? (
                        <TagsTitleContainer>
                            <RewardFunctionSelect
                                name="select-reward-function"
                                rewardFunctions={rewardFunctions ?? []}
                                handleChange={(value: string) => {
                                    onTitleChangeMutation({
                                        reward_function_id: value,
                                    });
                                }}
                                initialValue={formula?.reward_function_id}
                                key={formula?.reward_function_id}
                            />

                            <TopicListDropdown
                                handleChange={(_, value) =>
                                    handleChange(
                                        (value as SearchableSelectType)
                                            ?.value as string,
                                    )
                                }
                                selectedTopicValue={formula?.topic_id || ''}
                                isDisabled={isDisabled}
                            />
                        </TagsTitleContainer>
                    ) : (
                        <TagsTitleSingleContainer>
                            <TopicListDropdown
                                handleChange={(_, value) =>
                                    handleChange(
                                        (value as SearchableSelectType)
                                            ?.value as string,
                                    )
                                }
                                selectedTopicValue={formula?.topic_id || ''}
                                isDisabled={isDisabled}
                            />
                        </TagsTitleSingleContainer>
                    )}
                    <>
                        <TagsTitleContainer>
                            {formulaType === FormulaType.email && (
                                <>
                                    <FormulaTemplateSelector
                                        onTemplateIdChange={onTemplateIdChange}
                                        label="Choose a template"
                                    />

                                    <FormulaFromInput
                                        createOrUpdateFormula={
                                            onTitleChangeMutation
                                        }
                                        isDisabled={isDisabled}
                                    />
                                </>
                            )}
                        </TagsTitleContainer>
                    </>

                    <FormulaLabelsSection
                        formulaId={formula?.id}
                        formulaLabels={formula?.labels ?? []}
                        createOrUpdateFormula={onTitleChangeMutation}
                        hasWriteAccess={hasWriteAccess}
                    />
                </>
            ) : (
                <FormulaViewSetupForm
                    formikProps={formikProps}
                    hasWriteAccess={hasWriteAccess}
                />
            )}
        </Container>
    );
};

export default FormulaSetup;

const Container = styled.div({
    display: 'flex',
    width: '100%',
    alignItems: 'start',
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
});

const TagsTitleContainer = styled.div({
    display: 'flex',
    width: '100%',
    gap: aampeTheme.spacing(3),
    alignItems: 'flex-start',
});

const TagsTitleSingleContainer = styled.div({
    display: 'flex',
    width: '50%',
    gap: aampeTheme.spacing(3),
    justifyContent: 'start',
    alignItems: 'start',
});
